import type { getApiReactHooks } from '@frontend/api-client';
import type { DraftTask, KnownErrorStatusCode } from '@frontend/api-types';
import { makeSavedFileList } from '@frontend/forms';
import { useLoadingTimeouts } from '@frontend/react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import type { CreateDraftTaskState } from '../types';
import { convertAIQuestionnaireToInitialQuestionsWithAnswers } from '../utils';

export const initialState: CreateDraftTaskState = {
  attachments: [],
  clientAddressTimezone: undefined,
  description: undefined,
  draftTaskEntity: undefined,
  example: undefined,
  initialQuestionsWithAnswers: [],
  isScopeCheckLoading: false,
  letterRecommendationId: undefined,
  letterRecommendationTaskSuggestionIdx: undefined,
  name: undefined,
  proceedDecision: undefined,
  scopeFailureReason: undefined,
  selectedTimeSlot: undefined,
  suggestionId: undefined,
};

type CreateDraftTaskContextType = {
  isRetrieveDraftTaskLoading: boolean;
  manualRefetchScopeCheck: () => void;
  setState: (state: Partial<CreateDraftTaskState> | ((prev: CreateDraftTaskState) => CreateDraftTaskState)) => void;
  showScopeCheckStuckButton: boolean;
  state: CreateDraftTaskState;
  syncDraftTask: (draftTaskId: string, addId?: boolean) => Promise<MaybeUndefined<DraftTask>>;
};

const CreateDraftTaskContext = createContext<CreateDraftTaskContextType | undefined>(undefined);

export const useCreateDraftTask = () => {
  const context = useContext(CreateDraftTaskContext);
  if (!context) {
    throw new Error('useCreateDraftTask must be used within a CreateDraftTaskProvider');
  }
  return context;
};

type DraftTaskUnavailableDetails = {
  draftTaskId: string;
  error: unknown;
};

const draftUnavailableStatusCodes: KnownErrorStatusCode[] = [404, 409];

const isDraftTaskUnavailableError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const status = (error as { status?: number }).status;
  return typeof status === 'number' && draftUnavailableStatusCodes.includes(status as KnownErrorStatusCode);
};

export const CreateDraftTaskProvider = ({
  children,
  onDraftTaskUnavailable,
  onNavigateToTask,
  useLazyRetrieveDraftTaskQuery,
}: {
  children: React.ReactNode;
  onDraftTaskUnavailable?: (details: DraftTaskUnavailableDetails) => void;
  onNavigateToTask?: (taskId: string) => void;
  useLazyRetrieveDraftTaskQuery: ReturnType<typeof getApiReactHooks>['useLazyRetrieveDraftTaskQuery'];
}) => {
  const [state, setState] = useState<CreateDraftTaskState>(initialState);
  const [showScopeCheckStuckButton, setShowScopeCheckStuckButton] = useState<boolean>(false);
  const [retrieveDraftTask, { isFetching: isRetrieveDraftTaskLoading }] = useLazyRetrieveDraftTaskQuery();

  const setStateHandler = (
    newState: Partial<CreateDraftTaskState> | ((prev: CreateDraftTaskState) => CreateDraftTaskState),
  ) => {
    if (typeof newState === 'function') {
      setState(newState);
    } else {
      setState((prevState) => ({
        ...prevState,
        ...newState,
      }));
    }
  };

  const syncDraftTask = async (draftTaskId: string) => {
    const draftTask = await retrieveDraftTask({ draftTaskId })
      .unwrap()
      .catch((error) => {
        if (isDraftTaskUnavailableError(error) && onDraftTaskUnavailable) {
          onDraftTaskUnavailable({ draftTaskId, error });
        }
        return undefined;
      });

    if (draftTask) {
      setStateHandler((prevState) => ({
        ...prevState,
        attachments: makeSavedFileList(draftTask.attachmentUrls, (a) => ({
          ...a,
          key: a.id,
          thumbnails: a.thumbnails,
          url: a.contentUrl,
        })),
        clientAddressTimezone: draftTask.targetTimezone ?? undefined,
        description: prevState.description ?? draftTask.description,
        draftTaskEntity: draftTask,
        initialQuestionsWithAnswers: draftTask.aiQuestionnaire
          ? convertAIQuestionnaireToInitialQuestionsWithAnswers(draftTask.aiQuestionnaire.responses)
          : [],
        name: draftTask.name,
        proceedDecision: prevState.proceedDecision ?? draftTask.proceedDecision,
        scopeFailureReason: draftTask.scopeFailureReason ?? undefined,
      }));

      // Check if task is ready and navigate if callback is provided
      if (draftTask.task && onNavigateToTask) {
        onNavigateToTask(draftTask.task);
      }

      return draftTask;
    }
    return undefined;
  };

  const { manualRefetch } = useLoadingTimeouts({
    backoffStrategy: 'constant',
    isLoading: state.isScopeCheckLoading,
    maxRetries: 3,
    onRefetchTimeout: async () => {
      if (state.draftTaskEntity?.id) {
        await syncDraftTask(state.draftTaskEntity.id);
      }
    },
    onShowButtonTimeout: () => {
      setShowScopeCheckStuckButton(true);
    },
    refetchTimeoutMs: 3000,
    shouldTrigger: Boolean(state.draftTaskEntity?.id),
    showButtonTimeoutMs: 10000,
  });

  // Reset stuck button when scope check loading stops
  useEffect(() => {
    if (!state.isScopeCheckLoading) {
      setShowScopeCheckStuckButton(false);
    }
  }, [state.isScopeCheckLoading]);

  return (
    <CreateDraftTaskContext.Provider
      value={{
        isRetrieveDraftTaskLoading,
        manualRefetchScopeCheck: manualRefetch,
        setState: setStateHandler,
        showScopeCheckStuckButton,
        state,
        syncDraftTask,
      }}
    >
      {children}
    </CreateDraftTaskContext.Provider>
  );
};
